import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  // Sunrise/sunset times (stored as hours, e.g., 6.5 = 6:30 AM)
  const [sunTimes, setSunTimes] = useState(() => {
    const saved = localStorage.getItem("sunTimes");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return { sunrise: 6, sunset: 18 }; // Default: 6 AM - 6 PM
      }
    }
    return { sunrise: 6, sunset: 18 };
  });

  // Track if user has manually overridden the theme for current period
  const [manualOverride, setManualOverride] = useState(false);
  const manualOverrideRef = useRef(false);
  
  // The actual displayed theme: "light" or "dark"
  const [theme, setTheme] = useState("dark");
  
  // Track the last known period (day/night) to detect changes
  const lastPeriodRef = useRef(null);

  // Calculate if it's daytime based on current hour and sun times
  const isDaytime = useCallback(() => {
    const now = new Date();
    const currentHour = now.getHours() + now.getMinutes() / 60;
    return currentHour >= sunTimes.sunrise && currentHour < sunTimes.sunset;
  }, [sunTimes]);

  // Get the automatic theme based on time of day
  const getAutoTheme = useCallback(() => {
    return isDaytime() ? "light" : "dark";
  }, [isDaytime]);

  // Update theme based on time and override status
  const updateTheme = useCallback(() => {
    const currentPeriod = isDaytime() ? "day" : "night";
    const autoTheme = getAutoTheme();
    
    // Check if period changed (day -> night or night -> day)
    if (lastPeriodRef.current !== null && lastPeriodRef.current !== currentPeriod) {
      // Period changed - reset manual override
      setManualOverride(false);
      manualOverrideRef.current = false;
    }
    lastPeriodRef.current = currentPeriod;
    
    // If no manual override, use auto theme
    if (!manualOverrideRef.current) {
      setTheme(autoTheme);
    }
  }, [isDaytime, getAutoTheme]);

  // Toggle theme (manual override)
  const toggleTheme = useCallback(() => {
    manualOverrideRef.current = true;
    setManualOverride(true);
    setTheme(prev => prev === "light" ? "dark" : "light");
  }, []);

  // Update theme periodically and on mount
  useEffect(() => {
    updateTheme();
    
    // Check every minute for period changes
    const interval = setInterval(updateTheme, 60000);
    return () => clearInterval(interval);
  }, [updateTheme]);

  // Apply theme class to document
  useEffect(() => {
    const root = document.documentElement;
    
    if (theme === "light") {
      root.classList.add("light");
      root.classList.remove("dark");
    } else {
      root.classList.add("dark");
      root.classList.remove("light");
    }
  }, [theme]);

  // Update sun times (called when weather data is received)
  const updateSunTimes = useCallback((sunrise, sunset, timezoneOffset = 0) => {
    // Convert Unix timestamps to local hours using the location's timezone
    const sunriseDate = new Date((sunrise + timezoneOffset) * 1000);
    const sunsetDate = new Date((sunset + timezoneOffset) * 1000);
    
    // Use getUTCHours since we've already applied the offset
    const sunriseHour = sunriseDate.getUTCHours() + sunriseDate.getUTCMinutes() / 60;
    const sunsetHour = sunsetDate.getUTCHours() + sunsetDate.getUTCMinutes() / 60;
    
    const newSunTimes = { sunrise: sunriseHour, sunset: sunsetHour };
    setSunTimes(newSunTimes);
    localStorage.setItem("sunTimes", JSON.stringify(newSunTimes));
    
  }, []);

  // Format time for display (e.g., 6.5 -> "6:30 AM")
  const formatSunTime = useCallback((hour) => {
    const h = Math.floor(hour);
    const m = Math.round((hour % 1) * 60);
    const period = h >= 12 ? "PM" : "AM";
    const displayHour = h > 12 ? h - 12 : (h === 0 ? 12 : h);
    return `${displayHour}:${m.toString().padStart(2, '0')} ${period}`;
  }, []);

  return (
    <ThemeContext.Provider value={{ 
      theme,
      toggleTheme,
      updateSunTimes,
      sunTimes,
      isDaytime: isDaytime(),
      manualOverride,
      formatSunTime,
      getAutoTheme
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};
